import { Box, Button, Grid, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useStore } from '@roc/client-portal-shared/hooks';
import { useBaseStore } from '@roc/feature-app-core';
import { SignatureField, SignatureFieldHandle } from '@roc/ui';
import { formatCurrency } from '@roc/ui/utils';
import { format } from 'date-fns';
import { observer } from 'mobx-react';
import { ToWords } from 'to-words';
import { WetSignRequiredNotice } from '../wetSign/wetSignRequiredNotice';
import { isTpoWithMersLoan } from '../wetSign/wetSignConstants';

const useStyles = makeStyles(theme => ({
  container: {
    maxWidth: 1000,
    margin: 'auto',
    padding: theme.spacing(6),
    backgroundColor: '#fff',
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: theme.spacing(2),
    boxShadow: '0 4px 10px rgba(0,0,0,0.05)',
    overflow: 'auto',
    position: 'relative',
  },
  title: {
    fontWeight: 700,
    fontSize: '1.6rem',
    marginBottom: theme.spacing(4),
    textAlign: 'center',
  },
  titleLeft: {
    fontWeight: 700,
    fontSize: '1.6rem',
    marginBottom: theme.spacing(4),
    textAlign: 'left',
  },
  paragraph: {
    fontSize: '0.7rem',
    lineHeight: 1.7,
    marginBottom: theme.spacing(3),
    color: theme.palette.text.primary,
  },
  rightJustified: {
    fontSize: '0.7rem',
    lineHeight: 1.7,
    marginBottom: theme.spacing(3),
    color: theme.palette.text.primary,
    textAlign: 'right',
  },
  sectionHeader: {
    fontWeight: 600,
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(2),
    fontSize: '1rem',
  },
  submitButton: {
    color: '#fff',
    backgroundColor: theme.palette.primary.main,
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(4),
    width: '25%',
  },
}));

interface Props {
  signatureRef: React.RefObject<SignatureFieldHandle>;
  loan: any;
  handleSubmit: any;
}

export const NoteAllongeContentPage = observer(({ signatureRef, loan, handleSubmit }: Props) => {
  const classes = useStyles();
  const toWords = new ToWords({
    localeCode: 'en-US',
    converterOptions: {
      currency: true,
      doNotAddOnly: true,
      currencyOptions: {
        name: 'Dollar',
        plural: 'Dollars',
        symbol: '$',
        fractionalUnit: {
          name: 'Cent',
          plural: 'Cents',
          symbol: '',
        },
      },
    },
  });
  const { globalStore } = useBaseStore();

  const loanId = loan?.loanId;
  const loanAmountInWords = toWords.convert(loan?.amount);
  const loanAmountNumeric = formatCurrency(loan?.amount);
  const borrowerName = loan?.borrowerEntity?.company?.name;
  const propertyAddresses = loan?.properties ?? [];
  const companyName = globalStore.lenderInfo?.companyName;
  const expectedClosingDate = loan?.expectedClosingDate;

  return (
    <>
      <Box className={classes.container}>
        <Typography className={classes.title}>
          NOTE ALLONGE
        </Typography>

        <Typography className={classes.paragraph}>
          <b>ALLONGE TO THAT CERTAIN NOTE DATED: {expectedClosingDate}</b>
        </Typography>

        <Typography className={classes.paragraph}>
          <b>LOAN NUMBER: {loanId}</b>
        </Typography>

        <Typography className={classes.paragraph}>
          <b>IN THE AMOUNT OF: </b> {loanAmountInWords}
          <br/>
          ({loanAmountNumeric}) Dollars
        </Typography>

        <Typography className={classes.paragraph}>
          <b>BORROWER NAME: </b> {borrowerName}
        </Typography>

        <Typography className={classes.paragraph}>
          <b>COLLATERAL ADDRESS: </b>
          {propertyAddresses.map(property => {
            return(
              <>
                {property.address}
                <br/>
              </>
          )})}
        </Typography>

        <Typography className={classes.paragraph}>
          <b>FROM: {companyName}</b>
        </Typography>

        <Typography className={classes.paragraph}>
          <b>TO: Loan Funder LLC, Series {loanId}</b>
        </Typography>

        <Typography className={classes.paragraph}>
          PAY TO THE ORDER OF: Loan Funder LLC, Series {loanId}, without recourse and without representation or warranty of any kind, express or implied.
        </Typography>

        <Typography className={classes.paragraph}>
          ON {expectedClosingDate}
        </Typography>

        <Typography className={classes.rightJustified}>
          <b> {companyName}</b>
        </Typography>

        <SignatureField ref={signatureRef} penColor="green" />
      </Box>
      {isTpoWithMersLoan(loan) && (
        <Box maxWidth={1000} mx="auto" mt={4}>
          <WetSignRequiredNotice />
        </Box>
      )}
      <Grid container style={{ justifyContent: 'center' }}>
        <Button
          variant="contained"
          className={classes.submitButton}
          onClick={handleSubmit}
        >
          SUBMIT
        </Button>
      </Grid>
    </>
  );
});